import { Image, Upload, X } from "lucide-react";
import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Block } from "@/types/proposals";
import { useAuth } from "@/hooks/useAuth";

interface ImageBlockProps {
  block: Block;
  onUpdate: (updates: Partial<Block>) => void;
  onSelect: () => void;
}

export function ImageBlock({ block, onUpdate, onSelect }: ImageBlockProps) {
  const { url, alt } = block.content;
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { user } = useAuth();

  const style = block.style || {};

  const handleFileSelect = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      if (!user) throw new Error("Not authenticated");

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const { data, error } = await supabase.storage
        .from('template-images')
        .upload(fileName, file);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('template-images')
        .getPublicUrl(data.path);

      onUpdate({
        content: {
          ...block.content,
          url: publicUrl,
          alt: file.name,
        }
      });

      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleRemoveImage = () => {
    onUpdate({
      content: {
        ...block.content,
        url: null,
        alt: '',
      }
    });
  };

  const blockSize = block.settings?.blockSize || 'medium';
  const textAlign = block.style?.textAlign || 'left';

  const sizeClasses = {
    small: { 
      container: 'max-w-[200px] min-h-[150px]', 
      imageHeight: '8rem' 
    },
    medium: { 
      container: 'max-w-[400px] min-h-[200px]', 
      imageHeight: '12rem' 
    },
    large: { 
      container: 'max-w-[600px] min-h-[300px]', 
      imageHeight: '16rem' 
    },
  }[blockSize];

  const sizeClass = sizeClasses.container;
  const imageHeight = sizeClasses.imageHeight;

  const containerAlignClass = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  }[textAlign];

  return (
    <div 
      className={`flex ${containerAlignClass}`}
    >
      <div
        className={`relative ${sizeClass} cursor-pointer group`}
        onClick={onSelect}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {url ? (
          <>
            <img 
              src={url} 
              alt={alt || ""} 
              style={{
                width: 'auto',
                maxWidth: '100%',
                maxHeight: imageHeight,
                objectFit: 'contain',
                display: 'block',
              }}
            />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              <Upload className="w-4 h-4 mr-2" />
              Replace
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveImage();
              }}
            >
              <X className="w-4 h-4 mr-2" />
              Remove
            </Button>
          </div>
        </>
      ) : (
        <div 
          className={`w-full flex flex-col items-center justify-center bg-muted/30 border-2 border-dashed transition-colors ${sizeClass} ${
            isDragging ? 'border-primary bg-primary/10' : 'border-border'
          }`}
        >
          {uploading ? (
            <div className="w-full max-w-xs px-4">
              <Upload className="w-12 h-12 mx-auto mb-3 text-muted-foreground animate-pulse" />
              <Progress value={uploadProgress} className="mb-2" />
              <p className="text-sm text-center text-muted-foreground">
                Uploading... {uploadProgress}%
              </p>
            </div>
          ) : (
            <>
              <Image className="w-12 h-12 mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-2">
                {isDragging ? 'Drop image here' : 'Click or drag to upload'}
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Image
              </Button>
            </>
          )}
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileInputChange}
      />
      </div>
    </div>
  );
}